/**
 * Thêm / sửa khách và "Lưu ý cố định" ở web quầy (STT 2.1, ADR-089).
 *
 *  - `ToChonThem`: chủ tiệm bấm "Thêm" → chọn "Thêm khách" hoặc "Nhập danh sách khách".
 *  - `ToThemKhach`: tên + số + lưu ý, một nút "Lưu". Trùng số → "Số này đã có hồ sơ: … Mở hồ sơ đó?".
 *  - `ManSuaKhach`: màn con của hồ sơ khách — sửa tên / lưu ý (mọi người có quyền đặt lịch) và số (chủ / quản lý).
 *  - `KhoiLuuY`: MỘT khối dùng chung cho hồ sơ khách, tờ chi tiết lịch và tờ thu tiền (máy chủ đã che số theo vai).
 *
 * Không import từ GoiVi.tsx (GoiVi.tsx import tệp này) — tránh vòng.
 */
import { FileSpreadsheet, StickyNote, UserPlus, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { type Api, ApiFailure, chuanHoaSdt, hienSdt, type KhachMoi } from './api.ts'
import {
  cauDaLuuSua,
  coTheLuuKhach,
  type FormKhach,
  giuSoLienMach,
  type KhachGoc,
  kiemFormKhach,
  LUU_Y_CHU_TOI_DA,
  luuYSeGui,
  seMatLuuYKhiMoHoSo,
  soChuConLai,
  thanSuaKhach,
  thanThemKhach,
} from './khach-form.ts'
import { khoaChoNoiDung } from './nhap-khach-hien.ts'
import { KH } from './text/khach.ts'
import { T } from './text/vi.ts'
import { ThongBao, To } from './ui.tsx'

/** Khối "Lưu ý cố định". Ẩn khi rỗng. Chữ xuống dòng giữ nguyên, không tràn ngang. */
export function KhoiLuuY({ luuY }: { luuY: string | null | undefined }) {
  if (luuY === null || luuY === undefined || luuY.trim() === '') return null
  return (
    <section className="luu-y-co-dinh" aria-label={KH.luuY}>
      <StickyNote size={20} aria-hidden="true" />
      <div className="luu-y-chu">
        <b>{KH.luuY}</b>
        {/* Số điện thoại trong lưu ý đứng nguyên một cụm, không bẻ giữa dòng ở màn hẹp. */}
        <p>{giuSoLienMach(luuY)}</p>
      </div>
    </section>
  )
}

/** Câu lỗi cho người dùng: mất mạng / 5xx → câu "mạng chậm"; còn lại câu tiếng Việt của máy chủ. */
function cauLoiLuu(e: unknown): string {
  if (e instanceof ApiFailure) {
    if (e.status === 0 || e.status >= 500) return KH.mangCham
    return e.messageVi
  }
  return T.serverFailed
}

// ============================================================================= Tờ chọn "Thêm"

export function ToChonThem({
  onDong,
  onThemKhach,
  onNhapDs,
}: {
  onDong: () => void
  onThemKhach: () => void
  onNhapDs: () => void
}) {
  return (
    <To nhan={KH.them} onDong={onDong}>
      <header className="to-dau">
        <h2>{KH.them}</h2>
        <button
          type="button"
          className="nut nut-nhat nut-icon nut-icon-lon"
          aria-label={T.close}
          onClick={onDong}
        >
          <X size={22} />
        </button>
      </header>
      <div className="to-than">
        <button type="button" className="chon-them" onClick={onThemKhach}>
          <span className="vong-icon-nho">
            <UserPlus size={22} />
          </span>
          <span className="chu">
            <b>{KH.themKhach}</b>
            <span>{KH.themKhachPhu}</span>
          </span>
        </button>
        <button type="button" className="chon-them" onClick={onNhapDs}>
          <span className="vong-icon-nho">
            <FileSpreadsheet size={22} />
          </span>
          <span className="chu">
            <b>{KH.nhapDs}</b>
            <span>{KH.nhapDsPhu}</span>
          </span>
        </button>
      </div>
    </To>
  )
}

// ============================================================================= Trùng số → "Mở hồ sơ"

/**
 * Chân tờ khi số đã có hồ sơ. "Mở hồ sơ" là nút chính; nếu người dùng vừa gõ một lưu ý (sẽ mất khi mở hồ sơ
 * khác) thì hỏi MỘT lần "Bỏ lưu ý vừa gõ?" — không bỏ trong im lặng.
 */
function ChanMoHoSo({
  hoi,
  coLuuYGo,
  onHoi,
  onMo,
}: {
  hoi: boolean
  coLuuYGo: boolean
  onHoi: (b: boolean) => void
  onMo: () => void
}) {
  if (hoi) {
    return (
      <footer className="to-chan hai-nut">
        <button type="button" className="nut nut-vien nut-lon" onClick={() => onHoi(false)}>
          {KH.quayLaiForm}
        </button>
        <button type="button" className="nut nut-chinh nut-lon" onClick={onMo}>
          {KH.boLuuYMoHoSo}
        </button>
      </footer>
    )
  }
  return (
    <footer className="to-chan">
      <button
        type="button"
        className="nut nut-chinh nut-lon"
        onClick={() => (coLuuYGo ? onHoi(true) : onMo())}
      >
        {KH.moHoSo}
      </button>
    </footer>
  )
}

// ============================================================================= Ô nhập dùng chung

function OKhach({
  f,
  onDoi,
  loi,
  khoaSo = false,
  luuYCu = null,
  tuLayNet,
}: {
  f: FormKhach
  onDoi: (p: Partial<FormKhach>) => void
  loi: { ten: string | null; sdt: string | null; luuY: string | null }
  /** Vai không được đổi số: ô chỉ đọc + câu giải thích. */
  khoaSo?: boolean
  /**
   * Lễ tân chỉ được THÊM vào lưu ý: lưu ý có sẵn hiện chỉ-đọc, ô nhập là phần thêm vào. `null` = vai sửa tự do
   * (hoặc lưu ý đang trống).
   */
  luuYCu?: string | null
  tuLayNet: 'ten' | 'luuY'
}) {
  const chiThem = luuYCu !== null
  const conLai = soChuConLai(luuYSeGui(luuYCu, f.luuY, chiThem) ?? '')
  const refTen = useRef<HTMLInputElement>(null)
  const refLuuY = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    if (tuLayNet === 'ten') refTen.current?.focus()
    else refLuuY.current?.focus()
  }, [tuLayNet])
  return (
    <>
      <div className="truong">
        <label className="nhan" htmlFor="khach-ten">
          {KH.ten}
        </label>
        <input
          id="khach-ten"
          ref={refTen}
          className="o-nhap"
          type="text"
          autoComplete="off"
          maxLength={200}
          placeholder={KH.tenGoiY}
          value={f.ten}
          aria-invalid={loi.ten !== null}
          onChange={(e) => onDoi({ ten: e.target.value })}
        />
        {loi.ten !== null && <p className="chu-phu chu-loi">{loi.ten}</p>}
      </div>
      <div className="truong">
        <label className="nhan" htmlFor="khach-sdt">
          {KH.sdt}
        </label>
        <input
          id="khach-sdt"
          className="o-nhap"
          type="tel"
          inputMode="tel"
          autoComplete="off"
          maxLength={40}
          placeholder={KH.sdtGoiY}
          value={f.sdt}
          readOnly={khoaSo}
          aria-invalid={loi.sdt !== null}
          onChange={(e) => onDoi({ sdt: e.target.value })}
        />
        {khoaSo && <p className="chu-phu">{KH.chiChuDoiSo}</p>}
        {loi.sdt !== null && <p className="chu-phu chu-loi">{loi.sdt}</p>}
      </div>
      <div className="truong">
        {chiThem && <KhoiLuuY luuY={luuYCu} />}
        <label className="nhan" htmlFor="khach-luu-y">
          {chiThem ? KH.themVaoLuuY : KH.luuY}
        </label>
        <textarea
          id="khach-luu-y"
          ref={refLuuY}
          className="o-nhap"
          rows={3}
          maxLength={LUU_Y_CHU_TOI_DA}
          spellCheck={false}
          placeholder={KH.luuYGoiY}
          value={f.luuY}
          aria-invalid={loi.luuY !== null}
          onChange={(e) => onDoi({ luuY: e.target.value })}
        />
        {loi.luuY !== null ? (
          <p className="chu-phu chu-loi">{loi.luuY}</p>
        ) : (
          <>
            <p className="chu-phu">{chiThem ? KH.luuYChiThem : KH.luuYPhu}</p>
            <p className="chu-phu">{KH.conChu(conLai)}</p>
          </>
        )}
      </div>
    </>
  )
}

// ============================================================================= Tờ "Thêm khách"

export function ToThemKhach({
  api,
  dienSan,
  onDong,
  onXong,
  onMoHoSo,
}: {
  api: Api
  /** Điền sẵn từ ô tìm không ra: gõ là số → ô Số, gõ là chữ → ô Tên. */
  dienSan?: Partial<FormKhach>
  onDong: () => void
  /** Đã thêm xong: người gọi đóng tờ, báo, và mở hồ sơ khách vừa tạo. */
  onXong: (k: KhachMoi) => void
  /** Trùng số: mở hồ sơ đã có. */
  onMoHoSo: (id: string) => void
}) {
  const [f, setF] = useState<FormKhach>({ ten: '', sdt: '', luuY: '', ...dienSan })
  const [dang, setDang] = useState(false)
  const [loiTo, setLoiTo] = useState<string | null>(null)
  const [trung, setTrung] = useState<{ id: string; ten: string } | null>(null)
  const [daBamLuu, setDaBamLuu] = useState(false)
  const [hoiBoLuuY, setHoiBoLuuY] = useState(false)
  // Khoá chống lặp theo NỘI DUNG: bấm lại sau lỗi mạng = cùng khoá (không thêm hai lần); sửa chữ = khoá mới.
  const khoa = useRef<{ noiDung: string; khoa: string } | null>(null)
  const dangRef = useRef(false)

  const loi = kiemFormKhach(f)
  // Chỉ nhắc "gõ tên" sau khi người dùng đã bấm Lưu một lần — ô trống lúc mới mở không đỏ.
  const loiHien = { ...loi, ten: daBamLuu ? loi.ten : null }
  const hopLe = coTheLuuKhach(f)

  async function luu() {
    setDaBamLuu(true)
    if (dangRef.current || !hopLe) return
    dangRef.current = true
    setDang(true)
    setLoiTo(null)
    setTrung(null)
    const than = thanThemKhach(f)
    khoa.current = khoaChoNoiDung(khoa.current, JSON.stringify(than), () => crypto.randomUUID())
    try {
      const k = await api.themKhach(than, khoa.current.khoa)
      onXong(k)
    } catch (e) {
      if (e instanceof ApiFailure && e.khachTrung !== null) setTrung(e.khachTrung)
      else setLoiTo(cauLoiLuu(e))
    } finally {
      dangRef.current = false
      setDang(false)
    }
  }

  return (
    <To nhan={KH.themKhach} onDong={onDong}>
      <header className="to-dau">
        <h2>{KH.themKhach}</h2>
        <button
          type="button"
          className="nut nut-nhat nut-icon nut-icon-lon"
          aria-label={T.close}
          onClick={onDong}
        >
          <X size={22} />
        </button>
      </header>
      <form
        id="form-them-khach"
        className="to-than form-khach"
        onSubmit={(e) => {
          e.preventDefault()
          void luu()
        }}
      >
        {loiTo !== null && (
          <ThongBao loai="loi" lon onDong={() => setLoiTo(null)}>
            {loiTo}
          </ThongBao>
        )}
        {trung !== null && (
          <ThongBao loai="canh-bao" lon>
            {hoiBoLuuY ? KH.boLuuYHoi : KH.daCoHoSo(trung.ten)}
          </ThongBao>
        )}
        <OKhach
          f={f}
          onDoi={(p) => {
            // Đổi số thì "số này đã có hồ sơ" không còn đúng — trả nút "Lưu" về.
            if (p.sdt !== undefined) {
              setTrung(null)
              setHoiBoLuuY(false)
            }
            setF((c) => ({ ...c, ...p }))
          }}
          loi={loiHien}
          tuLayNet="ten"
        />
      </form>
      {trung !== null ? (
        // Trùng số: việc đúng là mở hồ sơ đã có — nút chính; "Lưu" ẩn cho tới khi đổi số.
        <ChanMoHoSo
          hoi={hoiBoLuuY}
          coLuuYGo={seMatLuuYKhiMoHoSo(f)}
          onHoi={setHoiBoLuuY}
          onMo={() => onMoHoSo(trung.id)}
        />
      ) : (
        <footer className="to-chan">
          <button
            type="submit"
            form="form-them-khach"
            className="nut nut-chinh nut-lon"
            disabled={dang || (daBamLuu && !hopLe)}
          >
            {dang ? KH.dangLuu : KH.luu}
          </button>
        </footer>
      )}
    </To>
  )
}

// ============================================================================= Màn con "Sửa khách"

export function ManSuaKhach({
  api,
  customerId,
  goc,
  coDoiSo,
  focus,
  onXong,
  onMoHoSo,
  onLoi,
}: {
  api: Api
  customerId: string
  goc: KhachGoc
  /** Chủ tiệm / quản lý: sửa được số. */
  coDoiSo: boolean
  focus: 'ten' | 'luuY'
  onXong: (cau: string) => void
  onMoHoSo: (id: string) => void
  onLoi: (s: string) => void
}) {
  // Ô số hiện dạng đọc to; số bị che (`0912***678`) hay chưa có số → trống (chỉ chủ / quản lý mới sửa, và họ thấy đủ số).
  const soHienCo = goc.sdt === null ? '' : hienSdt(chuanHoaSdt(goc.sdt) ?? '')
  // Lễ tân (không đổi được số) và lưu ý đã có chữ: chỉ được THÊM vào lưu ý (máy chủ cũng kiểm).
  const luuYCuChiThem = !coDoiSo && (goc.luuY ?? '').trim() !== '' ? (goc.luuY ?? '') : null
  const chiThem = luuYCuChiThem !== null
  const [f, setF] = useState<FormKhach>({
    ten: goc.ten,
    sdt: soHienCo,
    luuY: chiThem ? '' : (goc.luuY ?? ''),
  })
  const [dang, setDang] = useState(false)
  const [trung, setTrung] = useState<{ id: string; ten: string } | null>(null)
  const [hoiBoLuuY, setHoiBoLuuY] = useState(false)
  const dangRef = useRef(false)
  // Số bị che (`0912***678`): không sửa được từ đây — tránh "xoá số" nhầm khi ô hiện trống.
  const soBiChe = goc.sdt !== null && chuanHoaSdt(goc.sdt) === null
  const goc2: KhachGoc = {
    ...goc,
    sdt: goc.sdt === null ? null : (chuanHoaSdt(goc.sdt) ?? goc.sdt),
  }
  const loi = kiemFormKhach(f, goc2, chiThem)
  const than = thanSuaKhach(goc2, f, coDoiSo && !soBiChe, chiThem)
  const hopLe = coTheLuuKhach(f, goc2, chiThem) && than !== null

  async function luu() {
    if (dangRef.current || !hopLe || than === null) return
    dangRef.current = true
    setDang(true)
    setTrung(null)
    try {
      const r = await api.suaKhach(customerId, than)
      onXong(cauDaLuuSua(r, than))
    } catch (e) {
      if (e instanceof ApiFailure && e.khachTrung !== null) setTrung(e.khachTrung)
      else onLoi(cauLoiLuu(e))
    } finally {
      dangRef.current = false
      setDang(false)
    }
  }

  return (
    <>
      <form
        id="form-sua-khach"
        className="to-than form-khach"
        onSubmit={(e) => {
          e.preventDefault()
          void luu()
        }}
      >
        {trung !== null && (
          <ThongBao loai="canh-bao" lon>
            {hoiBoLuuY ? KH.boLuuYHoi : KH.daCoHoSo(trung.ten)}
          </ThongBao>
        )}
        <OKhach
          f={f}
          onDoi={(p) => {
            if (p.sdt !== undefined) {
              setTrung(null)
              setHoiBoLuuY(false)
            }
            setF((c) => ({ ...c, ...p }))
          }}
          loi={loi}
          khoaSo={!coDoiSo || soBiChe}
          luuYCu={luuYCuChiThem}
          tuLayNet={focus}
        />
      </form>
      {trung !== null ? (
        <ChanMoHoSo
          hoi={hoiBoLuuY}
          coLuuYGo={seMatLuuYKhiMoHoSo(f, goc2, chiThem)}
          onHoi={setHoiBoLuuY}
          onMo={() => onMoHoSo(trung.id)}
        />
      ) : (
        <footer className="to-chan">
          <button
            type="submit"
            form="form-sua-khach"
            className="nut nut-chinh nut-lon"
            disabled={dang || !hopLe}
          >
            {dang ? KH.dangLuu : KH.luu}
          </button>
        </footer>
      )}
    </>
  )
}
